import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { api, errMsg } from "@/lib/api";

function Preview({ draft }: { draft: any }) {
  const [tpl, setTpl] = useState<"field_estimate" | "quote_request_confirmation">("field_estimate");
  const [p, setP] = useState<any>(null);
  const [err, setErr] = useState("");
  useEffect(() => {
    const t = setTimeout(() => {
      api.post("/orgs/me/email-branding/preview", { ...draft, template: tpl }).then((r) => { setP(r.data); setErr(""); }).catch((e) => setErr(errMsg(e)));
    }, 350);
    return () => { clearTimeout(t); };
  }, [draft, tpl]);
  return (
    <div className="space-y-2" data-testid="email-preview">
      <div className="flex gap-2">
        {([["field_estimate", "Field Quote estimate"], ["quote_request_confirmation", "Homeowner confirmation"]] as const).map(([k, l]) => (
          <Button key={k} type="button" size="sm" variant={tpl === k ? "default" : "outline"} onClick={() => setTpl(k)} data-testid={`email-preview-${k}`}>{l}</Button>
        ))}
      </div>
      {err && <p className="text-sm text-destructive" data-testid="email-preview-error">{err}</p>}
      {p && (
        <div className="border rounded-lg overflow-hidden">
          <div className="text-xs bg-secondary px-3 py-2 space-y-0.5" data-testid="email-preview-headers">
            <p><b>From:</b> <span data-testid="email-preview-from">{p.from}</span></p>
            <p><b>Reply-To:</b> <span data-testid="email-preview-reply-to">{p.reply_to}</span></p>
            <p><b>Subject:</b> {p.subject}</p>
          </div>
          <iframe title="Email preview" sandbox="" srcDoc={p.html} className="w-full h-[520px] bg-white" data-testid="email-preview-frame" />
        </div>
      )}
    </div>
  );
}

function ReplyTo({ v, onChange }: { v: any; onChange: (d: any) => void }) {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const run = async (fn: () => Promise<any>, ok: string) => {
    setBusy(true);
    try { const { data } = await fn(); onChange(data); toast.success(ok); setEmail(""); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-2" data-testid="reply-to-section">
      <Label className="text-xs">Reply-To email (homeowner replies go here)</Label>
      <p className="text-sm" data-testid="reply-to-effective"><b>{v.effective_reply_to}</b> {v.reply_to ? <span className="text-primary">· confirmed</span> : <span className="text-muted-foreground">· default (confirmed)</span>}</p>
      {v.default_pending && <p className="text-xs text-amber-700" data-testid="default-reply-to-pending">Your new lead notification email {v.default_pending} receives lead alerts now, but becomes the homeowner Reply-To only after you click the confirmation link we emailed to it.</p>}
      {v.reply_to_pending && <p className="text-xs text-amber-700" data-testid="reply-to-pending">Waiting for confirmation: {v.reply_to_pending}. Open the link we emailed to that address.</p>}
      <div className="flex flex-col sm:flex-row gap-2">
        <Input type="email" placeholder="replies@yourbusiness.com" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="reply-to-input" />
        <Button type="button" variant="outline" disabled={busy || !email} onClick={() => run(() => api.post("/orgs/me/email-branding/reply-to", { email }), "Confirmation email sent")} data-testid="reply-to-request">Send confirmation</Button>
      </div>
      {(v.reply_to || v.reply_to_pending) && (
        <button type="button" className="text-xs underline text-muted-foreground" disabled={busy} onClick={() => run(() => api.delete("/orgs/me/email-branding/reply-to"), "Reply-To reset to default")} data-testid="reply-to-reset">Use my default address ({v.default_reply_to}) instead</button>
      )}
      <p className="text-[11px] text-muted-foreground">A new address is only used after someone clicks the confirmation link sent to it. Emails are still sent from our verified domain.</p>
    </div>
  );
}

export function EmailBranding() {
  const [v, setV] = useState<any>(null);
  const [d, setD] = useState<any>(null);
  const [busy, setBusy] = useState(false);
  const load = (data: any) => { setV(data); setD({ sender_name: data.sender_name, signature_message: data.signature_message, use_logo: data.use_logo, show_phone: data.show_phone, show_website: data.show_website }); };
  useEffect(() => { api.get("/orgs/me/email-branding").then((r) => load(r.data)).catch((e) => toast.error(errMsg(e))); }, []);
  if (!v || !d) return null;
  const save = async () => {
    setBusy(true);
    try { load((await api.patch("/orgs/me/email-branding", d)).data); toast.success("Email branding saved"); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const sw = (k: string, label: string, disabled = false) => (
    <label className="flex items-center gap-3 text-sm"><Switch checked={!!d[k]} disabled={disabled} onCheckedChange={(c) => setD({ ...d, [k]: c })} data-testid={`branding-${k}-switch`} />{label}</label>
  );
  return (
    <section className="bg-card border rounded-xl p-6 space-y-5 mt-6" data-testid="email-branding">
      <div><h2 className="font-semibold">Email Branding</h2><p className="text-sm text-muted-foreground">How your estimates and quote confirmations look to homeowners.</p></div>
      <div className="space-y-1.5">
        <Label className="text-xs" htmlFor="sender_name">Sender display name</Label>
        <Input id="sender_name" maxLength={60} placeholder={v.business_name} value={d.sender_name} onChange={(e) => setD({ ...d, sender_name: e.target.value })} data-testid="branding-sender-name-input" />
        <p className="text-[11px] text-muted-foreground">Shown as “{(d.sender_name || v.business_name).trim() || v.business_name} via Instant Quote Wizard”. Leave blank to use your business name.</p>
      </div>
      <ReplyTo v={v} onChange={load} />
      <div className="space-y-2">
        {sw("use_logo", v.has_logo ? "Show my business logo" : "Show my business logo (upload one under Appearance)", !v.has_logo)}
        {sw("show_phone", v.phone ? `Show phone (${v.phone}) in signature` : "Show phone in signature (add one above)")}
        {sw("show_website", v.website ? `Show website in signature` : "Show website in signature (add one above)")}
      </div>
      <div className="space-y-1.5">
        <Label className="text-xs" htmlFor="signature_message">Signature message (optional, plain text)</Label>
        <Textarea id="signature_message" rows={3} maxLength={300} value={d.signature_message} onChange={(e) => setD({ ...d, signature_message: e.target.value })} placeholder="Thanks for considering us — we look forward to working with you." data-testid="branding-signature-input" />
        <p className="text-[11px] text-muted-foreground">{(d.signature_message || "").length}/300 · formatting and links are not supported.</p>
      </div>
      <Button onClick={save} disabled={busy} data-testid="branding-save-button">{busy ? "Saving…" : "Save email branding"}</Button>
      <Preview draft={d} />
    </section>
  );
}
